/**
 * Checks if a user agent matches the regular expression
 *
 * @param {string} regex The regular expression to match against
 * @param {string} userAgent The user agent to be matched
 *
 * @return {boolean}
 */
function userAgentMatch (regex, userAgent = navigator.userAgent) {
  return userAgent.match(regex) !== null;
}

/**
 * Checks if the user agent is an MS Edge user agent
 *
 * @param {string} userAgent The user agent to check
 *
 * @return {boolean}
 */
export function isEdge (userAgent = navigator.userAgent) {
  return userAgentMatch(/\sEdge\/d+/i, userAgent);
}

/**
 * Checks if the user agent is an iOS user agent
 *
 * @param {string} userAgent The user agent to check
 *
 * @return {boolean}
 */
export function isIOS (userAgent = navigator.userAgent) {
  return userAgentMatch(/iP(?:hone|ad|od)/i, userAgent);
}

/**
 * Checks if the user agent is a Firefox user agent
 *
 * @param {string} userAgent The user agent to check
 *
 * @return {boolean}
 */
export function isFirefox (userAgent = navigator.userAgent) {
  return userAgentMatch(/gecko\//i, userAgent);
}

/**
 * Checks if the user agent is a Chrome user agent
 *
 * @param {string} userAgent The user agent to check
 *
 * @return {boolean}
 */
export function isChrome (userAgent = navigator.userAgent) {
  return userAgentMatch(/\s(?:(?:Headless)?Chrome|CriOS)\//i, userAgent) &&
    !isOpera(userAgent) &&
    !isEdge(userAgent) &&
    !userAgentMatch(/UCBrowser/i, userAgent);
}

/**
 * Checks if the user agent is a Safari user agent
 *
 * @param {string} userAgent The user agent to check
 *
 * @return {boolean}
 */
export function isSafari (userAgent = navigator.userAgent) {
  return userAgentMatch(/safari/i, userAgent) &&
    !isOpera(userAgent) &&
    !isEdge(userAgent) &&
    !isChrome(userAgent) &&
    !userAgentMatch(/(?:Chrome|CriOS|chromium|android|phantom)/i, userAgent);
}

/**
 * Checks if the user agent is an Android user agent
 *
 * @param {string} userAgent The user agent to check
 *
 * @return {boolean}
 */
export function isAndroid (userAgent = navigator.userAgent) {
  return userAgentMatch(/Android/i, userAgent) &&
    !userAgentMatch(/Windows Phone/i, userAgent);
}

/**
 * Checks if the user agent is a mobile user agent
 *
 * @param {string} userAgent The user agent to check
 *
 * @return {boolean}
 */
export function isMobile (userAgent = navigator.userAgent) {
  return isIOS(userAgent) || isAndroid(userAgent) || userAgentMatch(/Windows Phone/i, userAgent);
}

/**
 * Checks if the user agent is a native Android browser user agent
 *
 * @param {string} userAgent The user agent to check
 *
 * @return {boolean}
 */
export function isAndroidNative (userAgent = navigator.userAgent) {
  return (userAgentMatch(/chrome\/[1-9]/i, userAgent) &&
    !userAgentMatch(/chrome\/18/i, userAgent) &&
    !isFirefox)
    ? isAndroid
    : false;
}

/**
 * Checks if the window has been loaded in an iframe
 *
 * @return {boolean}
 */
export function isIframe () {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
}

/**
 * Checks if the user agent is an Internet Explorer user agent
 *
 * @param {string} userAgent The user agent to check
 *
 * @return {boolean}
 */
export function isIE (userAgent = navigator.userAgent) {
  return userAgentMatch(/msie/i, userAgent) ||
    userAgentMatch(/trident\/.+rv:\s*11/i);
}

/**
 * Checks if the user agent is a Windows user agent
 *
 * @param {string} userAgent The user agent to check
 *
 * @return {boolean}
 */
export function isWindows (userAgent = navigator.userAgent) {
  return isEdge(userAgent) || isIE(userAgent);
}

/**
 * Checks if the user agent is a Macintosh user agent
 *
 * @param {string} userAgent The user agent to check
 *
 * @return {boolean}
 */
export function isMacintosh (userAgent = navigator.userAgent) {
  return userAgentMatch(/Macintosh/i, userAgent);
}

/**
 * Checks if the user agent is an Opera user agent
 *
 * @param {string} userAgent The user agent to check
 *
 * @return {boolean}
 */
export function isOpera (userAgent = navigator.userAgent) {
  return userAgentMatch(/^Opera\/[._\d]+|Opera [._\d]+$|OPR\/[._\d]+/, userAgent);
}

/**
 * Builds the operating system version from a user agent
 *
 * @param {string} userAgent The user agent
 *
 * @return {object}
 */
export function platformVersion (userAgent = navigator.userAgent) {
  let major, minor, version;

  if (isWindows(userAgent)) {
    const match = /Windows(?: NT|)? ([._\d]+)/.exec(userAgent);
    if (match) {
      version = match[1];

      if (version === '6.1') {
        version = '7.0';
      }
      if (version === '6.2') {
        version = '8.0';
      }
      if (version === '6.3') {
        version = '8.1';
      }
    }
  }

  if (isAndroid(userAgent)) {
    const match = /Android ([._\d]+)/.exec(userAgent);
    if (match) {
      version = match[1];
    }
  }

  if (isIOS(userAgent)) {
    const match = /OS ([._\d]+)/.exec(userAgent);
    if (match) {
      version = match[1];
    }
  }

  if (isMacintosh(userAgent)) {
    const match = /Mac OS X ([._\d]+)/.exec(userAgent);
    if (match) {
      version = match[1];
    }
  }

  if (version) {
    const versionNumbers = version.split(/[._]/);

    if (versionNumbers.length > 1) {
      major = parseInt(versionNumbers[0], 10);
      minor = parseInt(versionNumbers[1], 10);
    } else {
      major = parseInt(version, 10);
      minor = 0;
    }
  }

  return { major, minor };
}

/**
 * Get the name of the platform by user agent
 *
 * @param {string} userAgent The user agent
 *
 * @return {string}
 */
export function platformName (userAgent = navigator.userAgent) {
  switch (true) {
    case isAndroid(userAgent):
      return 'android';
    case isIOS(userAgent):
      return 'ios';
    case isMacintosh(userAgent):
      return 'mac';
    case isWindows(userAgent):
      return 'win';
    default:
      return 'unknown';
  }
}

/**
 * Builds the browser version from a user agent
 *
 * @param {string} userAgent The user agent
 *
 * @return {object}
 */
export function browserVersion (userAgent = navigator.userAgent) {
  let version, major, minor;

  if (isChrome(userAgent)) {
    const index = userAgent.indexOf('Chrome');

    if (index !== -1) {
      version = userAgent.substring(index + 7);
    } else {
      version = userAgent.substring(userAgent.indexOf('CriOS') + 6);
    }
  }

  if (isSafari(userAgent)) {
    version = userAgent.substring(userAgent.indexOf('Version') + 8);
  }

  if (isFirefox(userAgent)) {
    version = userAgent.substring(userAgent.indexOf('Firefox') + 8);
  }

  if (isEdge(userAgent)) {
    version = userAgent.substring(userAgent.indexOf('Edge') + 5);
  }

  if (isIE(userAgent)) {
    if (userAgent.indexOf('rv:') !== -1) {
      version = userAgent.substring(userAgent.indexOf('rv:') + 3);
    } else {
      version = userAgent.substring(userAgent.indexOf('MSIE') + 5);
    }
  }

  if (isOpera(userAgent)) {
    const result = /^Opera\/([._\d]+)|Opera ([._\d]+)$|OPR\/([._\d]+)/.exec(userAgent);

    version = result[1] || result[2] || result[3];
  }

  if (version) {
    let ix;

    if ((ix = version.indexOf(';')) !== -1) {
      version = version.substring(0, ix);
    }
    if ((ix = version.indexOf(' ')) !== -1) {
      version = version.substring(0, ix);
    }
    if ((ix = version.indexOf(')')) !== -1) {
      version = version.substring(0, ix);
    }

    major = parseInt(version, 10);
    minor = parseInt(version.split('.')[1], 10);
  }

  return { major, minor };
}

/**
 * Get the name of the browser by user agent
 *
 * @param {string} userAgent The user agent
 *
 * @return {string}
 */
export function browserName (userAgent = navigator.userAgent) {
  switch (true) {
    case isChrome(userAgent):
      return 'chrome';
    case isFirefox(userAgent):
      return 'firefox';
    case isSafari(userAgent):
      return 'safari';
    case isEdge(userAgent):
      return 'edge';
    case isOpera(userAgent):
      return 'opera';
    case isIE(userAgent):
      return 'internet_explorer';
    case isAndroidNative(userAgent):
      return 'android_native';
    default:
      return 'unknown';
  }
}

/**
 * Get the availability of connection info in browser
 *
 * @param {string} connection The navigator connection
 *
 * @return {string}
 */
export function getNetworkInfo (connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection) {
  let isAvailable = false;
  let networkType = '';
  let networkDownlink = '';
  let networkDownlinkMax = '';
  let networkRtt = '';

  if (connection) {
    isAvailable = true;
    networkType = connection.effectiveType;
    networkDownlink = connection.downlink;
    networkDownlinkMax = connection.downlinkMax;
    networkRtt = connection.rtt;
  }

  return {
    isAvailable: isAvailable,
    effectiveType: networkType,
    downlink: networkDownlink,
    downlinkMax: networkDownlinkMax,
    rtt: networkRtt
  };
}

export default {
  isIframe: isIframe(),
  isMobile: isMobile(),

  browser: {
    name: browserName(),
    version: browserVersion(),

    isIE: isIE(),
    isEdge: isEdge(),
    isOpera: isOpera(),
    isChrome: isChrome(),
    isSafari: isSafari(),
    isFirefox: isFirefox(),
    isAndroidNative: isAndroidNative()
  },

  platform: {
    name: platformName(),
    version: platformVersion(),

    isAndroid: isAndroid(),
    isWindows: isWindows(),
    isIOS: isIOS(),
    isMacintosh: isMacintosh()
  },

  network: getNetworkInfo()
};
